import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PPAPendingUserMessage } from 'types/ppaViews';

type UsePendingContextMessagesParams = {
    viewMessages?: PPAPendingUserMessage[];
    lensMessages?: PPAPendingUserMessage[];
};

const mergePendingMessages = (
    viewMessages: PPAPendingUserMessage[],
    lensMessages: PPAPendingUserMessage[],
): PPAPendingUserMessage[] => {
    const seen = new Set<number>();
    const merged: PPAPendingUserMessage[] = [];

    [...viewMessages, ...lensMessages].forEach((message) => {
        if (seen.has(message.message_id)) {
            return;
        }
        seen.add(message.message_id);
        merged.push(message);
    });

    return merged;
};

export function usePendingContextMessages({
    viewMessages = [],
    lensMessages = [],
}: UsePendingContextMessagesParams) {
    const incomingMessages = useMemo(
        () => mergePendingMessages(viewMessages, lensMessages),
        [lensMessages, viewMessages],
    );
    const incomingIds = useMemo(
        () => incomingMessages.map((message) => message.message_id).join(','),
        [incomingMessages],
    );

    const [queue, setQueue] = useState<PPAPendingUserMessage[]>([]);
    const [dismissedIds, setDismissedIds] = useState<Set<number>>(() => new Set());

    useEffect(() => {
        setQueue((prev) => {
            const next = incomingMessages.filter((message) => !dismissedIds.has(message.message_id));
            const prevIds = prev.map((message) => message.message_id).join(',');
            const nextIds = next.map((message) => message.message_id).join(',');
            return prevIds === nextIds ? prev : next;
        });
    }, [dismissedIds, incomingIds, incomingMessages]);

    const currentMessage = queue[0] ?? null;

    const dismiss = useCallback((messageId: number) => {
        setDismissedIds((prev) => new Set(prev).add(messageId));
        setQueue((prev) => prev.filter((message) => message.message_id !== messageId));
    }, []);

    const dismissCurrent = useCallback(() => {
        if (!currentMessage) {
            return;
        }
        dismiss(currentMessage.message_id);
    }, [currentMessage, dismiss]);

    return {
        messages: queue,
        currentMessage,
        dismiss,
        dismissCurrent,
        hasPendingMessages: queue.length > 0,
    };
}

export default usePendingContextMessages;
